/**
 * Session identity utilities.
 *
 * Generates a random session ID on first access and stores it in sessionStorage
 * so that each browser session has a unique identity.  The ID is sent as
 * X-Session-Id header with every API request.
 *
 * This is a stopgap measure -- real authentication will replace it later.
 */

const SESSION_KEY = 'sm_session_id'

/**
 * Return the current session ID, creating one if it doesn't exist yet.
 * Uses sessionStorage so the ID is scoped to the browser tab/session.
 */
export function getSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY)
    if (!sid) {
      sid = `${Date.now()}-${crypto.randomUUID()}`
      sessionStorage.setItem(SESSION_KEY, sid)
    }
    return sid
  } catch {
    // Fallback if sessionStorage is unavailable (e.g. sandboxed iframe)
    return `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
}

/**
 * Build the standard session headers expected by the backend.
 * Includes the JWT bearer token when one is available.
 */
export function getSessionHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Session-Id': getSessionId(),
  }

  try {
    const token = localStorage.getItem('structura.auth.token') || ''
    if (token.length > 0) {
      headers['Authorization'] = `Bearer ${token}`
    }
  } catch {
    // ignore
  }

  return headers
}
